import React, { useState } from 'react';

/**
 * ShareBar — reusable social share component
 * Props:
 *   text      — the pre-filled share text
 *   url       — the URL to share (defaults to knowfi.io)
 *   compact   — if true, icon-only buttons (no labels)
 *   variant   — 'default' | 'quiz' | 'win'
 */
export default function ShareBar({ text, url = 'https://knowfi.io', compact = false, variant = 'default' }) {
  const [copied, setCopied] = useState(false);

  const encodedText = encodeURIComponent(text);
  const encodedUrl  = encodeURIComponent(url);

  const share = (platform) => {
    let href = '';
    if (platform === 'x')         href = `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    if (platform === 'facebook')  href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
    if (platform === 'linkedin')  href = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedText}`;
    if (platform === 'reddit')    href = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`;
    if (href) window.open(href, '_blank', 'width=600,height=500,noopener,noreferrer');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${text}\n\n${url}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const accent = variant === 'quiz' ? '#a78bfa' : variant === 'win' ? '#4ade80' : '#38bdf8';

  const btnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#cbd5e1', borderRadius: '8px', padding: compact ? '6px 10px' : '7px 14px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.15s', textDecoration: 'none',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span style={{ color: '#475569', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
        Share:
      </span>

      <button style={{ ...btnStyle, color: '#e2e8f0' }} onClick={() => share('x')} title="Share on X">
        𝕏 {!compact && 'Post'}
      </button>

      <button style={{ ...btnStyle }} onClick={() => share('facebook')} title="Share on Facebook">
        <span style={{ color: '#60a5fa' }}>f</span> {!compact && 'Share'}
      </button>

      <button style={{ ...btnStyle }} onClick={() => share('linkedin')} title="Share on LinkedIn">
        <span style={{ color: '#38bdf8' }}>in</span> {!compact && 'Share'}
      </button>

      <button style={{ ...btnStyle }} onClick={() => share('reddit')} title="Share on Reddit">
        <span style={{ color: '#ff4500' }}>●</span> {!compact && 'Reddit'}
      </button>

      <button
        style={{ ...btnStyle, color: copied ? accent : '#94a3b8', borderColor: copied ? accent : 'rgba(255,255,255,0.1)' }}
        onClick={copyLink}
        title="Copy link"
      >
        {copied ? '✓ Copied!' : '🔗' + (compact ? '' : ' Copy')}
      </button>
    </div>
  );
}
